import {ErrorMessage, Field, getIn} from 'formik'
import {ToolTipUI} from '../UI/ToolTipUI'
import clsx from 'clsx'
import {ToolTipUIForModal} from '../UI/ToolTipUIForModal'
import {useIntl} from 'react-intl'

// Define the props interface allowing for both plain values and objects
interface SelectInputProps<T = {[key: string]: any}> {
  fieldName: string
  label?: string
  toolTipText?: string
  placeholder: string
  formik: any
  options: T[] | string[] // Accept both arrays of objects and arrays of strings
  margin?: string
  isFieldArray?: boolean
  isTooltipNotRequired?: boolean
  width?: number
  isStarRequired?: boolean
  isSearchIcon?: boolean
  disabled?: boolean
  isTooltipForModal?: boolean
  valueKey?: keyof T
  labelKey?: keyof T
  id?: keyof T
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onChange?: (e: React.FocusEvent<HTMLInputElement>) => void
  disabledFileTypes?: string[]
}

export const SelectInput = <T extends {[key: string]: any}>({
  fieldName,
  label,
  toolTipText,
  placeholder,
  formik,
  options,
  margin,
  isFieldArray,
  isTooltipNotRequired,
  width,
  isStarRequired,
  isSearchIcon,
  disabled,
  isTooltipForModal,
  valueKey = 'value' as keyof T,
  labelKey = 'name' as keyof T,
  id,
  onBlur,
  onChange,
  disabledFileTypes,
}: SelectInputProps<T>) => {
  const FieldArrayErrorMessage = ({name}: {name: string}) => (
    <Field
      name={name}
      render={({form}: {form: any}) => {
        const error = getIn(form.errors, name)
        const touch = getIn(form.touched, name)
        return touch && error ? error : null
      }}
    />
  )

  // Determine if the options are objects or plain strings
  const isOptionsObjectArray = options?.length > 0 && typeof options[0] === 'object'
  const {formatMessage} = useIntl()

  return (
    <div className={`fv-row mb-7 ${margin ? margin : ''} ${width ? `col-md-${width}` : ''}`}>
      {label && (
        <label className='d-flex form-label font-size-13 text-dark text-capitalize ms-md-0'>
          {isStarRequired ? (
            !isTooltipNotRequired ? (
              <>
                {label}*{' '}
                {isTooltipForModal ? (
                  <ToolTipUIForModal tooltipText={formatMessage({id: toolTipText})} />
                ) : (
                  <ToolTipUI tooltipText={toolTipText} />
                )}
              </>
            ) : (
              <>{label}*</>
            )
          ) : !isTooltipNotRequired ? (
            <>
              {label}{' '}
              {isTooltipForModal ? (
                <ToolTipUIForModal tooltipText={formatMessage({id: toolTipText})} />
              ) : (
                <ToolTipUI tooltipText={toolTipText} />
              )}
            </>
          ) : (
            <>{label}</>
          )}
        </label>
      )}

      <Field
        as='select'
        name={fieldName}
        onBlur={onBlur}
        disabled={disabled}
        value={formik.values[fieldName]}
        onChange={(e: any) => {
          if (onChange) {
            onChange(e)
          }
          formik.setFieldValue(fieldName, e.target.value)
        }}
        className={clsx(
          `${isSearchIcon ? 'form-search-select' : ''} form-select h-48px font-size-13`,
          {'is-invalid-local': formik.touched[fieldName] && formik.errors[fieldName]},
          {'is-valid-local': formik.touched[fieldName] && !formik.errors[fieldName]}
        )}
      >
        <option className='text-muted' hidden>
          {placeholder}
        </option>
        {isOptionsObjectArray
          ? (options as T[])?.map((option, index) => (
              <option
                key={index}
                id={option.id}
                value={option.value}
                hidden={disabledFileTypes ? !disabledFileTypes.includes(option.value) : false}
              >
                {option[labelKey]}
              </option>
            ))
          : (options as string[])?.map((option, index) => (
              <option
                key={index}
                value={option}
                hidden={disabledFileTypes ? !disabledFileTypes.includes(option) : false}
              >
                {option}
              </option>
            ))}
      </Field>
      <div className='text-danger mt-2'>
        {isFieldArray ? (
          <FieldArrayErrorMessage name={fieldName} />
        ) : (
          <ErrorMessage name={fieldName} />
        )}
      </div>
    </div>
  )
}
